import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isEqual } from 'lodash';

import { loadGoogleMapsApi, resetGoogleMapsApi } from '@admin/components/ops_map/google_maps';
import MAP_STYLES from '@admin/react/components/DispatchLiveDashboard/mapOptions.json';

import { DispatcherMapMarkerService } from './dispatcher_map_marker_service';
import {
  DISPATCHER_MAP_COLORS,
  DISPATCHER_MAP_DEFAULT_CENTER,
  DispatcherMapDepot,
  plotDispatch,
  plotUndispatchedOrder,
} from './dispatcher_map_plotting';
import { DispatcherDispatchRecord, DispatcherLayout } from './dispatcher_types';

const MAPS_LOAD_ERROR_MESSAGE = 'Unable to load Google Maps. Please try again.';

// Shared with OpsMap / DispatchLiveDashboard map styling.

type DispatcherMapProps = {
  layout: DispatcherLayout;
  depots: DispatcherMapDepot[];
  regionCenter?: { latitude: number | string; longitude: number | string } | null;
};

const dispatchMapKey = (dispatch: DispatcherDispatchRecord) => dispatch.mapID ?? dispatch.id;

const extendBoundsForAddress = (
  bounds: google.maps.LatLngBounds,
  address?: { latitude?: number | string | null; longitude?: number | string | null } | null,
) => {
  if (address?.latitude == null || address?.longitude == null) {
    return false;
  }

  bounds.extend({
    lat: Number(address.latitude),
    lng: Number(address.longitude),
  });
  return true;
};

const fitMapToVisiblePoints = (
  map: google.maps.Map,
  dispatches: DispatcherDispatchRecord[],
  undispatchedOrders: DispatcherLayout['undispatched']['orders'],
  depots: DispatcherMapDepot[],
  regionCenter?: { latitude: number | string; longitude: number | string } | null,
) => {
  const bounds = new google.maps.LatLngBounds();
  let hasPoint = false;

  dispatches.forEach((dispatch) => {
    const depot = depots.find((entry) => String(entry.id) === String(dispatch.depot?.id));
    if (extendBoundsForAddress(bounds, depot?.address)) {
      hasPoint = true;
    }

    dispatch.orders.forEach((order) => {
      if (extendBoundsForAddress(bounds, order.address)) {
        hasPoint = true;
      }
    });
  });

  undispatchedOrders.forEach((order) => {
    if (extendBoundsForAddress(bounds, order.address)) {
      hasPoint = true;
    }
  });

  if (hasPoint) {
    map.fitBounds(bounds, 48);
    return;
  }

  if (regionCenter?.latitude != null && regionCenter?.longitude != null) {
    map.setCenter({
      lat: Number(regionCenter.latitude),
      lng: Number(regionCenter.longitude),
    });
    map.setZoom(11);
    return;
  }

  map.setCenter(DISPATCHER_MAP_DEFAULT_CENTER);
  map.setZoom(11);
};

export const DispatcherMap: React.FC<DispatcherMapProps> = ({ layout, depots, regionCenter }) => {
  const [mapEnabled, setMapEnabled] = useState(false);
  const [mapsLoadState, setMapsLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [focusedRouteKeys, setFocusedRouteKeys] = useState<string[]>([]);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const routesByDispatchRef = useRef<Record<string | number, google.maps.DirectionsRenderer>>({});
  const markerServiceRef = useRef(new DispatcherMapMarkerService());
  const highlightedRouteKeyRef = useRef<string | null>(null);
  const openInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const plotGenerationRef = useRef(0);

  const activeDispatches = useMemo(
    () => layout.dispatches.filter((dispatch) => !dispatch._destroy),
    [layout.dispatches],
  );

  const activeRouteKeys = useMemo(
    () =>
      activeDispatches
        .map((dispatch) => dispatchMapKey(dispatch))
        .filter((key): key is string | number => key != null)
        .map(String),
    [activeDispatches],
  );

  useEffect(() => {
    setFocusedRouteKeys((previous) => (isEqual(previous, activeRouteKeys) ? previous : activeRouteKeys));
  }, [activeRouteKeys]);

  const focusedDispatches = useMemo(
    () =>
      activeDispatches.filter((dispatch) => {
        const key = dispatchMapKey(dispatch);
        return key != null && focusedRouteKeys.includes(String(key));
      }),
    [activeDispatches, focusedRouteKeys],
  );

  const anyRoutesFocused = useCallback(
    () => highlightedRouteKeyRef.current != null || focusedRouteKeys.length !== activeRouteKeys.length,
    [activeRouteKeys.length, focusedRouteKeys],
  );

  const openInfoWindow = useCallback((infoWindow: google.maps.InfoWindow, marker: google.maps.Marker) => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    openInfoWindowRef.current?.close();
    openInfoWindowRef.current = infoWindow;
    infoWindow.open(map, marker);
  }, []);

  const viewAllRoutes = useCallback(() => {
    highlightedRouteKeyRef.current = null;
    openInfoWindowRef.current?.close();
    setFocusedRouteKeys(activeRouteKeys);
  }, [activeRouteKeys]);

  const resetMap = useCallback(() => {
    Object.values(routesByDispatchRef.current).forEach((route) => route.setMap(null));
    routesByDispatchRef.current = {};
    markerServiceRef.current.clearMarkers();
  }, []);

  const setupMap = useCallback(
    ({ fitBounds = true }: { fitBounds?: boolean } = {}) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      const plotGeneration = plotGenerationRef.current + 1;
      plotGenerationRef.current = plotGeneration;
      const isPlotCurrent = (generation: number) => generation === plotGenerationRef.current;

      resetMap();
      mapClickListenerRef.current?.remove();
      mapClickListenerRef.current = google.maps.event.addListener(map, 'click', () => {
        openInfoWindowRef.current?.close();
        if (anyRoutesFocused()) {
          viewAllRoutes();
        }
      });

      focusedDispatches.forEach((dispatch) => {
        plotDispatch({
          dispatch,
          routeIndex: activeDispatches.indexOf(dispatch),
          depots,
          map,
          routesByDispatch: routesByDispatchRef.current,
          markerService: markerServiceRef.current,
          onMarkerRouteFocus: (mapKey) => {
            highlightedRouteKeyRef.current = String(mapKey);
          },
          openInfoWindow,
          plotGeneration,
          isPlotCurrent,
        });
      });

      layout.undispatched.orders.forEach((order) => {
        plotUndispatchedOrder(order, map, markerServiceRef.current, openInfoWindow);
      });

      if (fitBounds) {
        fitMapToVisiblePoints(map, focusedDispatches, layout.undispatched.orders, depots, regionCenter);
      }
    },
    [
      activeDispatches,
      anyRoutesFocused,
      depots,
      focusedDispatches,
      layout.undispatched.orders,
      openInfoWindow,
      regionCenter,
      resetMap,
      viewAllRoutes,
    ],
  );

  const setupMapRef = useRef(setupMap);
  setupMapRef.current = setupMap;

  const initializeMap = useCallback(async () => {
    if (!mapContainerRef.current) {
      return;
    }

    setMapsLoadState('loading');
    try {
      await loadGoogleMapsApi();
      mapRef.current = new google.maps.Map(mapContainerRef.current, {
        center: DISPATCHER_MAP_DEFAULT_CENTER,
        zoom: 11,
        styles: MAP_STYLES,
      });
      setMapsLoadState('ready');
      setupMap({ fitBounds: true });
    } catch {
      setMapsLoadState('error');
    }
  }, [setupMap]);

  const handleToggleMap = useCallback(() => {
    if (!mapEnabled) {
      setMapEnabled(true);
      return;
    }

    setMapEnabled(false);
    resetMap();
    mapRef.current = null;
    setMapsLoadState('idle');
  }, [mapEnabled, resetMap]);

  useEffect(() => {
    if (!mapEnabled || mapsLoadState !== 'idle') {
      return;
    }

    void initializeMap();
  }, [initializeMap, mapEnabled, mapsLoadState]);

  useEffect(() => {
    if (!mapEnabled || mapsLoadState !== 'ready') {
      return;
    }

    // Replot on editor changes without jumping back to the region center.
    setupMapRef.current({ fitBounds: false });
  }, [layout, mapEnabled, mapsLoadState]);

  useEffect(() => {
    if (!mapEnabled || mapsLoadState !== 'ready') {
      return;
    }

    setupMapRef.current({ fitBounds: true });
  }, [focusedRouteKeys, mapEnabled, mapsLoadState, regionCenter]);

  const handleFocusedRoutesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKeys = Array.from(event.target.selectedOptions).map((option) => option.value);
    setFocusedRouteKeys(selectedKeys);
  };

  const handleViewAllRoutes = () => {
    viewAllRoutes();
  };

  return (
    <div className="dispatcher-map">
      <button type="button" className="btn btn-default" onClick={handleToggleMap}>
        {mapEnabled ? 'Disable' : 'Enable'} Map
      </button>

      {mapEnabled && (
        <div className="dispatcher-route-focus">
          <button type="button" className="btn btn-default" onClick={handleViewAllRoutes}>
            View All Routes
          </button>
          <select name="routeFocus" multiple value={focusedRouteKeys} onChange={handleFocusedRoutesChange}>
            {activeDispatches.map((dispatch, index) => {
              const key = dispatchMapKey(dispatch);
              if (key == null) {
                return null;
              }

              return (
                <option
                  key={String(key)}
                  value={String(key)}
                  style={{ color: DISPATCHER_MAP_COLORS[index % DISPATCHER_MAP_COLORS.length] }}
                >
                  Route #{index + 1}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {mapEnabled && mapsLoadState === 'error' && (
        <div className="alert alert-danger mar-top" role="alert">
          {MAPS_LOAD_ERROR_MESSAGE}{' '}
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={() => {
              resetGoogleMapsApi();
              setMapsLoadState('idle');
            }}
          >
            Retry
          </button>
        </div>
      )}

      {mapEnabled && (
        <div
          ref={mapContainerRef}
          className="dispatcher-map-canvas"
          style={{ display: mapsLoadState === 'ready' ? 'block' : 'none', height: '400px', marginTop: '12px' }}
        />
      )}
    </div>
  );
};
